import React, {useCallback, useEffect, useState} from 'react';
import {Empty, List, Modal, Spin, Tag, Typography} from 'antd';
import {botService, EquityHistoryResponse} from '../../services/botService';
import {useDrag} from '../../hooks/useDrag';
import EquityBreakdownCharts from './EquityBreakdownCharts';

const {Text} = Typography;

interface EquityChartModalProps {
    visible: boolean;
    onClose: () => void;
    apiKeyId: number;
}

const EquityChartModal: React.FC<EquityChartModalProps> = ({visible, onClose, apiKeyId}) => {
    const [equityData, setEquityData] = useState<EquityHistoryResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [nonPnlBaselineVisible, setNonPnlBaselineVisible] = useState(true);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 767px)');
        setIsMobile(mq.matches);
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    const EQUITY_CHART_STORAGE_KEY = 'equity_chart_modal_position';

    const {dragState, handleMouseDown, resetPosition, modalStyle} = useDrag({
        longPressDelay: 500,
        storageKey: EQUITY_CHART_STORAGE_KEY,
        boundaryPadding: 50
    });

    const fetchEquityHistory = useCallback(async () => {
        if (!visible || !apiKeyId) return;
        setLoading(true);
        setError('');
        try {
            const response = await botService.getEquityHistory(apiKeyId, 500);
            if (response.success && response.data) {
                setEquityData(response.data);
            } else {
                setError(response.message || '获取权益数据失败');
            }
        } catch {
            setError('获取权益数据失败');
        } finally {
            setLoading(false);
        }
    }, [visible, apiKeyId]);

    useEffect(() => {
        if (visible) fetchEquityHistory();
    }, [visible, apiKeyId, fetchEquityHistory]);

    const formatTime = (timestamp: number) => {
        const date = new Date(timestamp);
        return `${date.getMonth() + 1}/${date.getDate()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    };

    const handleClose = () => {
        setEquityData(null);
        setError('');
        resetPosition();
        onClose();
    };

    const titleText = equityData?.provider && equityData?.accountType
        ? `${equityData.provider.toUpperCase()} ${equityData.accountType} 权益走势`
        : '账户权益';

    const getQualityTag = () => {
        switch (equityData?.quality) {
            case 'UNSUPPORTED':
                return <Tag color="orange">暂不支持</Tag>;
            case 'FETCH_FAILED':
                return <Tag color="red">当前采集失败</Tag>;
            case 'CONFIG_REQUIRED':
                return <Tag color="orange">需要配置</Tag>;
            case 'ESTIMATED':
                return <Tag color="yellow">估算</Tag>;
            default:
                return null;
        }
    };

    const hasPoints = !loading && !!equityData?.points?.length;
    const dexNoBreakdown = equityData?.latestBreakdown?.stablecoinEquity == null
        && equityData?.latestBreakdown?.otherAssetEquity == null;

    return (
        <Modal
            className="dark-theme-modal"
            title={
                <div
                    style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        cursor: dragState.isDragging ? 'grabbing' : 'grab', userSelect: 'none'
                    }}
                    onMouseDown={handleMouseDown}
                >
                    <span>{titleText}</span>
                    {!dragState.isDragging && getQualityTag()}
                </div>
            }
            open={visible}
            onCancel={handleClose}
            footer={null}
            width="calc(100vw - 64px)"
            styles={{
                mask: {backgroundColor: 'rgba(0, 0, 0, 0.7)'},
                content: {
                    backgroundColor: '#0d1117',
                    border: '1px solid rgba(255,255,255,0.13)',
                    borderRadius: 8,
                    maxWidth: 1200,
                    ...modalStyle
                },
                body: {
                    padding: '16px',
                    backgroundColor: '#0d1117',
                    height: isMobile ? '90vh' : '620px',
                    cursor: dragState.isDragging ? 'grabbing' : 'default'
                }
            }}
        >
            <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                gap: 16,
                flexDirection: isMobile ? 'column' : 'row'
            }}>
                <div style={{flex: 3, display: 'flex', flexDirection: 'column'}}>
                    <div style={{
                        flex: 1,
                        backgroundColor: '#141414',
                        border: '1px solid rgba(255,255,255,0.13)',
                        borderRadius: 4,
                        padding: '16px',
                        position: 'relative'
                    }}>
                        {hasPoints ? (
                            <EquityBreakdownCharts
                                points={equityData!.points}
                                quality={equityData!.quality}
                                dexNoBreakdown={dexNoBreakdown}
                                showNonPnlBaseline={nonPnlBaselineVisible}
                                onToggleNonPnlBaseline={setNonPnlBaselineVisible}
                            />
                        ) : loading ? (
                            <div style={{
                                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                display: 'flex', justifyContent: 'center', alignItems: 'center',
                                background: 'rgba(20,20,20,0.7)'
                            }}>
                                <Spin size="large">
                                    <div style={{marginTop: 8, color: '#999'}}>正在加载权益数据...</div>
                                </Spin>
                            </div>
                        ) : error ? (
                            <div style={{
                                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#ff5252'
                            }}>
                                {error}
                            </div>
                        ) : (
                            <div style={{
                                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                display: 'flex', justifyContent: 'center', alignItems: 'center'
                            }}>
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={
                                        equityData?.quality === 'UNSUPPORTED' ? '该数据源暂不支持权益查询'
                                            : equityData?.quality === 'FETCH_FAILED' ? '权益数据暂不可用'
                                                : equityData?.quality === 'CONFIG_REQUIRED' ? '请在API连接中配置钱包地址'
                                                    : '暂无权益数据'
                                    }
                                    style={{color: '#999'}}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div style={{
                    flex: 1, display: 'flex', flexDirection: 'column',
                    backgroundColor: '#141414', border: '1px solid rgba(255,255,255,0.13)',
                    borderRadius: 4, overflow: 'hidden'
                }}>
                    <div style={{
                        padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.13)',
                        backgroundColor: '#1a1a1a'
                    }}>
                        <Text style={{color: '#ddd', fontWeight: 'bold'}}>最近权益点</Text>
                    </div>
                    <div style={{flex: 1, overflow: 'auto'}}>
                        <List
                            size="small"
                            dataSource={equityData?.points ? [...equityData.points].reverse().slice(0, 20) : []}
                            renderItem={(item) => (
                                <List.Item style={{borderBottom: '1px solid #2a2a2a', padding: '8px 12px'}}>
                                    <div style={{width: '100%'}}>
                                        <div
                                            style={{display: 'flex', justifyContent: 'space-between', marginBottom: 4}}>
                                            <Text style={{color: '#999', fontSize: 11}}>{formatTime(item.time)}</Text>
                                            <Tag
                                                color={item.source === 'TRADE_EVENT' ? 'green' : item.source === 'MANUAL' ? 'blue' : 'default'}
                                                style={{margin: 0, fontSize: 10}}>{item.source}</Tag>
                                        </div>
                                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px'}}>
                                            <div style={{display: 'flex', justifyContent: 'space-between'}}>
                                                <Text style={{color: '#999', fontSize: 11}}>总权益</Text>
                                                <Text style={{
                                                    color: '#F0B429',
                                                    fontSize: 12,
                                                    fontWeight: 'bold'
                                                }}>{item.totalEquity.toFixed(2)}</Text>
                                            </div>
                                            <div style={{display: 'flex', justifyContent: 'space-between'}}>
                                                <Text style={{color: '#999', fontSize: 11}}>可用</Text>
                                                <Text style={{
                                                    color: '#00d97e',
                                                    fontSize: 11
                                                }}>{item.availableEquity.toFixed(2)}</Text>
                                            </div>
                                            <div style={{display: 'flex', justifyContent: 'space-between'}}>
                                                <Text style={{color: '#999', fontSize: 11}}>保证金</Text>
                                                <Text style={{
                                                    color: '#ff5252',
                                                    fontSize: 11
                                                }}>{item.usedMargin.toFixed(2)}</Text>
                                            </div>
                                            <div style={{display: 'flex', justifyContent: 'space-between'}}>
                                                <Text style={{color: '#999', fontSize: 11}}>未结盈亏</Text>
                                                <Text style={{
                                                    color: item.unsettledPnl != null && item.unsettledPnl >= 0 ? '#00d97e' : '#ff5252',
                                                    fontSize: 11,
                                                    fontWeight: 'bold'
                                                }}>
                                                    {item.unsettledPnl != null ? item.unsettledPnl.toFixed(2) : '--'}
                                                </Text>
                                            </div>
                                            {item.stablecoinEquity != null && (
                                                <div style={{display: 'flex', justifyContent: 'space-between'}}>
                                                    <Text style={{color: '#999', fontSize: 11}}>稳定币</Text>
                                                    <Text style={{
                                                        color: '#4d9fff',
                                                        fontSize: 11
                                                    }}>{item.stablecoinEquity.toFixed(2)}</Text>
                                                </div>
                                            )}
                                            {item.frozenEquity != null && (
                                                <div style={{display: 'flex', justifyContent: 'space-between'}}>
                                                    <Text style={{color: '#999', fontSize: 11}}>冻结</Text>
                                                    <Text style={{
                                                        color: '#f5a623',
                                                        fontSize: 11
                                                    }}>{item.frozenEquity.toFixed(2)}</Text>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </List.Item>
                            )}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default EquityChartModal;
